import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useLanguage } from "./i18n";

// ---------------------------------------------------------------------------
// Coordinate parsing helpers
// ---------------------------------------------------------------------------

function parseCoordValue(str, format) {
  str = (str || "").trim();
  if (!str) return NaN;

  const negative = /[SW]/i.test(str) || /^-/.test(str.replace(/\s/g, ""));
  const cleaned = str.replace(/[NSEWnsew+\-°'"´`\s]/g, "").replace(",", ".");

  if (format === "dd") {
    const val = parseFloat(str.replace(",", "."));
    return isNaN(val) ? NaN : negative ? -Math.abs(val) : Math.abs(val);
  }
  if (format === "dm") {
    // DDDMM.MMMM or DDMM.MMMM  →  3 integer degree-digits when intPart.length >= 5
    const intPart = cleaned.split(".")[0];
    if (intPart.length >= 4) {
      const degDigits = intPart.length >= 5 ? 3 : 2;
      const d = parseInt(cleaned.slice(0, degDigits), 10);
      const m = parseFloat(cleaned.slice(degDigits));
      const val = d + m / 60;
      return negative ? -val : val;
    }
    const match = str.match(/(-?\d+(?:[.,]\d+)?)\D+(\d+(?:[.,]\d+)?)/);
    if (match) {
      const d = parseFloat(match[1].replace(",", "."));
      const m = parseFloat(match[2].replace(",", "."));
      return (d < 0 ? -1 : 1) * (Math.abs(d) + m / 60);
    }
    return NaN;
  }
  if (format === "dms") {
    // DDDMMSS.ssss or DDMMSS.ssss  →  3 integer degree-digits when intPart.length >= 7
    const intPart = cleaned.split(".")[0];
    if (intPart.length >= 6) {
      const degDigits = intPart.length >= 7 ? 3 : 2;
      const d = parseInt(cleaned.slice(0, degDigits), 10);
      const m = parseInt(cleaned.slice(degDigits, degDigits + 2), 10);
      const s = parseFloat(cleaned.slice(degDigits + 2));
      const val = d + m / 60 + s / 3600;
      return negative ? -val : val;
    }
    const match = str.match(/(-?\d+(?:[.,]\d+)?)\D+(\d+)\D+(\d+(?:[.,]\d+)?)/);
    if (match) {
      const d = parseFloat(match[1].replace(",", "."));
      const m = parseFloat(match[2]);
      const s = parseFloat(match[3].replace(",", "."));
      return (d < 0 ? -1 : 1) * (Math.abs(d) + m / 60 + s / 3600);
    }
    return NaN;
  }
  return NaN;
}

function splitRow(row, delimiter, customDelimiter) {
  if (delimiter === "tab")       return row.split("\t");
  if (delimiter === "semicolon") return row.split(";");
  if (delimiter === "comma")     return row.split(",");
  if (delimiter === "space")     return row.trim().split(/\s+/);
  if (delimiter === "custom" && customDelimiter) return row.split(customDelimiter);
  return row.trim().split(/[\t;,\s]+/);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BulkImportTool({ geometryType, onImport, onCancel }) {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);

  const [rawText,     setRawText]     = useState("");
  const [fileName,    setFileName]    = useState("");
  const [delimiter,   setDelimiter]   = useState("comma");
  const [customDelim, setCustomDelim] = useState("");
  const [coordFormat, setCoordFormat] = useState("dd");
  const [hasHeader,   setHasHeader]   = useState(false);
  const [colRoles,    setColRoles]    = useState([]); // array indexed by column, value = role string
  const [radiusUnit,  setRadiusUnit]  = useState("meter");

  // ── Roles available per geometry ──────────────────────────────────────────
  const showRadiusCol = geometryType === "Circle";
  const availableRoles = useMemo(() => {
    const base = [
      { value: "ignore",    label: t("bulkImport_role_ignore") },
      { value: "name",      label: t("bulkImport_col_name") },
      { value: "lat",       label: t("bulkImport_col_lat") },
      { value: "lon",       label: t("bulkImport_col_lon") },
      { value: "alt",       label: t("bulkImport_col_alt") },
    ];
    if (showRadiusCol) base.push({ value: "radius", label: t("bulkImport_col_radius") });
    return base;
  }, [showRadiusCol, t]);

  // ── Parse raw text into header row + data rows ────────────────────────────
  const { displayHeaders, rows } = useMemo(() => {
    if (!rawText.trim()) return { displayHeaders: [], rows: [] };
    const lines = rawText.split("\n").map(l => l.trimEnd()).filter(l => l.trim());
    if (!lines.length) return { displayHeaders: [], rows: [] };

    const allRows = lines.map(l => splitRow(l, delimiter, customDelim).map(c => c.trim()));
    const maxCols = Math.max(...allRows.map(r => r.length));
    const padded = allRows.map(r => {
      const arr = [...r];
      while (arr.length < maxCols) arr.push("");
      return arr;
    });

    let displayHeaders, dataRows;
    if (hasHeader && padded.length > 0) {
      displayHeaders = padded[0];
      dataRows = padded.slice(1);
    } else {
      displayHeaders = Array.from({ length: maxCols }, (_, i) => `Col ${i + 1}`);
      dataRows = padded;
    }
    return { displayHeaders, rows: dataRows };
  }, [rawText, delimiter, customDelim, hasHeader]);

  // ── Auto-detect role for each column when headers change ──────────────────
  useEffect(() => {
    if (!displayHeaders.length) { setColRoles([]); return; }
    const roles = displayHeaders.map(h => {
      const l = h.toLowerCase();
      if (l.includes("lat") || l === "y")                          return "lat";
      if (l.includes("lon") || l.includes("lng") || l === "x")     return "lon";
      if (l.includes("alt") || l.includes("elev") || l === "z")    return "alt";
      if (l.includes("name") || l.includes("id"))                   return "name";
      if (showRadiusCol && l.includes("rad"))                        return "radius";
      return "ignore";
    });
    // Positional fallback: if nothing detected assign first cols to lat/lon/alt
    const hasLat = roles.includes("lat"), hasLon = roles.includes("lon");
    if (!hasLat && displayHeaders.length >= 1) roles[0] = "lat";
    if (!hasLon && displayHeaders.length >= 2) roles[1] = "lon";
    if (!roles.includes("alt") && displayHeaders.length >= 3) roles[2] = "alt";
    setColRoles(roles);
  }, [displayHeaders, showRadiusCol]);

  // ── Build column-index lookup from roles ──────────────────────────────────
  const colIdx = useMemo(() => {
    const m = { lat: -1, lon: -1, alt: -1, name: -1, radius: -1 };
    colRoles.forEach((role, i) => { if (role !== "ignore" && m[role] === -1) m[role] = i; });
    return m;
  }, [colRoles]);

  // ── Parse each row into a preview object ──────────────────────────────────
  const previewObjects = useMemo(() => {
    return rows.map((row, idx) => {
      const get = key => colIdx[key] >= 0 ? (row[colIdx[key]] || "") : "";
      const lat    = parseCoordValue(get("lat"),    coordFormat);
      const lon    = parseCoordValue(get("lon"),    coordFormat);
      const altRaw = get("alt");
      const alt    = altRaw ? parseFloat(altRaw.replace(",", ".")) : 0;
      const radRaw = get("radius");
      const radius = radRaw ? parseFloat(radRaw.replace(",", ".")) : NaN;
      const name   = get("name");
      const valid  = !isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180 && !!name.trim();
      return { rowIndex: idx + 1, lat, lon, alt, radius, name, valid, raw: row };
    });
  }, [rows, colIdx, coordFormat]);

  const validCount = previewObjects.filter(o => o.valid).length;
  const totalCount = previewObjects.length;

  // ── File upload ────────────────────────────────────────────────────────────
  function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = ev => setRawText(ev.target.result);
    reader.readAsText(file);
    e.target.value = "";
  }

  // ── Import ─────────────────────────────────────────────────────────────────
  function handleImport() {
    const valid = previewObjects.filter(o => o.valid);
    if (!valid.length) return;
    let toAdd = [];
    if (geometryType === "Point") {
      toAdd = valid.map(o => ({ geometry: { type: "Point", coordinates: [o.lon, o.lat, o.alt] }, properties: { name: o.name } }));
    } else if (geometryType === "Circle") {
      toAdd = valid.map(o => {
        const r = !isNaN(o.radius) ? o.radius * (radiusUnit === "NM" ? 1852 : 1) : 0;
        return { geometry: { type: "Circle", center: [o.lon, o.lat, o.alt], radius: r, z: o.alt }, properties: { name: o.name } };
      });
    } else if (geometryType === "LineString") {
      toAdd = [{ geometry: { type: "LineString", coordinates: valid.map(o => [o.lon, o.lat, o.alt]) }, properties: { name: valid[0]?.name || "" } }];
    } else if (geometryType === "Polygon") {
      toAdd = [{ geometry: { type: "Polygon", coordinates: [valid.map(o => [o.lon, o.lat, o.alt])] }, properties: { name: valid[0]?.name || "" } }];
    }
    onImport && onImport(toAdd);
  }

  const setRole = useCallback((colIndex, role) => {
    setColRoles(prev => {
      const next = [...prev];
      next[colIndex] = role;
      return next;
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="bulk-import-tool">

      {/* ── Settings bar ── */}
      <div className="bulk-import-settings-bar">
        {/* Delimiter */}
        <div className="bulk-import-settings-group">
          <span className="bulk-import-settings-label">{t("bulkImport_delimiter")}</span>
          {["semicolon","comma","space","tab","custom"].map(d => (
            <label key={d} className="bulk-import-radio-label">
              <input type="radio" name="bulk-delim" value={d} checked={delimiter === d} onChange={() => setDelimiter(d)} />
              {" "}{t(`bulkImport_delim_${d}`)}
            </label>
          ))}
          {delimiter === "custom" && (
            <input type="text" value={customDelim} onChange={e => setCustomDelim(e.target.value)}
                   placeholder={t("bulkImport_customDelimPlaceholder")} className="bulk-import-custom-delim-input" />
          )}
        </div>
        {/* Coord format */}
        <div className="bulk-import-settings-group">
          <span className="bulk-import-settings-label">{t("bulkImport_coordFormat")}</span>
          {[["dd","DD.dddddddd"],["dm","DDMM.MMMM"],["dms","DDMMSS.ssss"]].map(([v, l]) => (
            <label key={v} className="bulk-import-radio-label">
              <input type="radio" name="bulk-cfmt" value={v} checked={coordFormat === v} onChange={() => setCoordFormat(v)} />
              {" "}{l}
            </label>
          ))}
        </div>
        {/* Header + radius unit */}
        <div className="bulk-import-settings-group">
          <label className="bulk-import-radio-label">
            <input type="checkbox" checked={hasHeader} onChange={e => setHasHeader(e.target.checked)} />
            {" "}{t("bulkImport_hasHeader")}
          </label>
          {showRadiusCol && (
            <>
              <span className="bulk-import-settings-label" style={{ marginLeft: 8 }}>{t("bulkImport_radiusUnit")}</span>
              <label className="bulk-import-radio-label">
                <input type="radio" checked={radiusUnit === "NM"} onChange={() => setRadiusUnit("NM")} /> NM
              </label>
              <label className="bulk-import-radio-label">
                <input type="radio" checked={radiusUnit === "meter"} onChange={() => setRadiusUnit("meter")} /> meter
              </label>
            </>
          )}
        </div>
      </div>

      {/* ── File upload area ── */}
      <div className="bulk-import-dropzone" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
        <input ref={fileInputRef} type="file" accept=".csv,.txt,.tsv" style={{ display: "none" }} onChange={handleFileUpload} />
        {fileName
          ? <span className="bulk-import-dropzone-filename">📄 {fileName}</span>
          : <span className="bulk-import-dropzone-hint">{t("bulkImport_dropzoneHint")}</span>
        }
        <span className="bulk-import-dropzone-btn">{t("bulkImport_chooseFile")}</span>
      </div>

      {/* ── Paste area (collapsed if file loaded) ── */}
      {!fileName && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 3 }}>{t("bulkImport_orPaste")}</div>
          <textarea
            className="bulk-import-textarea"
            value={rawText}
            onChange={e => { setRawText(e.target.value); setFileName(""); }}
            placeholder={t("bulkImport_pastePlaceholder")}
            rows={5}
            spellCheck={false}
          />
        </div>
      )}
      {fileName && (
        <div style={{ marginBottom: 8 }}>
          <textarea
            className="bulk-import-textarea bulk-import-textarea--compact"
            value={rawText}
            onChange={e => setRawText(e.target.value)}
            rows={3}
            spellCheck={false}
          />
        </div>
      )}

      {/* ── Column-mapping table (shown as soon as data is parsed) ── */}
      {displayHeaders.length > 0 && rows.length > 0 && (
        <div className="bulk-import-table-wrap">
          <div className="bulk-import-section-title">
            {t("bulkImport_columnMapping")}
            {" – "}
            <span className={validCount === totalCount ? "bulk-import-count-ok" : "bulk-import-count-warn"}>
              {t("bulkImport_validOf").replace("{valid}", validCount).replace("{total}", totalCount)}
            </span>
          </div>
          {colIdx.name === -1 && (
            <div className="bulk-import-warning">{t("bulkImport_nameRequired")}</div>
          )}
          <div className="bulk-import-preview-wrap">
            <table className="bulk-import-preview-table">
              <thead>
                {/* Row 1: column role dropdowns */}
                <tr className="bulk-import-role-row">
                  <th className="bulk-import-th-idx">#</th>
                  {displayHeaders.map((h, ci) => (
                    <th key={ci} className="bulk-import-th-role">
                      <select
                        value={colRoles[ci] || "ignore"}
                        onChange={e => setRole(ci, e.target.value)}
                        className="bulk-import-role-select"
                        title={h}
                      >
                        {availableRoles.map(r => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </th>
                  ))}
                  <th className="bulk-import-th-status">{t("bulkImport_valid")}</th>
                </tr>
                {/* Row 2: original column header labels */}
                <tr className="bulk-import-header-label-row">
                  <th></th>
                  {displayHeaders.map((h, ci) => (
                    <th key={ci} className="bulk-import-th-label" title={h}>{h}</th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {previewObjects.slice(0, 100).map((o, ri) => (
                  <tr key={ri} className={o.valid ? "bulk-row-ok" : "bulk-row-err"}>
                    <td className="bulk-td-idx">{o.rowIndex}</td>
                    {o.raw.map((cell, ci) => {
                      const role = colRoles[ci] || "ignore";
                      return (
                        <td key={ci} className={`bulk-td-cell bulk-td-role-${role}`}>
                          {cell || <span className="bulk-cell-empty">—</span>}
                        </td>
                      );
                    })}
                    <td className="bulk-cell-status">{o.valid ? "✓" : "✗"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {previewObjects.length > 100 && (
              <div className="bulk-import-hint">{t("bulkImport_showingFirst100")}</div>
            )}
          </div>
        </div>
      )}

      {/* ── Actions ── */}
      <div className="bulk-import-actions">
        <button className="bulk-import-btn-primary" onClick={handleImport} disabled={validCount === 0 || colIdx.name === -1}>
          {t("bulkImport_import").replace("{count}", validCount)}
        </button>
        <button className="bulk-import-btn-secondary" onClick={onCancel}>
          {t("close")}
        </button>
      </div>
    </div>
  );
}
